"use client";

import { useState } from "react";
import { Notice, PillGroup } from "@/components/tool-kit/primitives";
import {
  FloatPanel,
  ForcePanel,
} from "@/components/tools/physics-fields/buoyancy-panels";
import {
  type SigChoice,
  SigFigsField,
} from "@/components/tools/physics-fields/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";

/** English strings. A translated content file overrides these through `ui`. */
export const BUOYANCY_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to find?",
  modeFloat: "Will it float? Fraction submerged and apparent weight",
  modeForce: "Buoyant force F = ρVg",
  materialLabel: "Object material",
  custom: "Custom value",
  materialNames: {
    cork: "Cork (about 240 kg/m³)",
    pine: "Pine wood (about 500 kg/m³)",
    oak: "Oak wood (about 750 kg/m³)",
    ice: "Ice (917 kg/m³)",
    glass: "Glass (about 2500 kg/m³)",
    concrete: "Concrete (about 2400 kg/m³)",
    aluminium: "Aluminium (2700 kg/m³)",
    steel: "Steel (about 7850 kg/m³)",
    copper: "Copper (8960 kg/m³)",
    lead: "Lead (11340 kg/m³)",
    gold: "Gold (19320 kg/m³)",
  },
  describeBy: "Describe the object by its",
  byDensity: "Density and volume",
  byMass: "Mass and volume",
  fluidLabel: "Fluid",
  planetLabel: "Gravity of",
  fluidNames: {
    "fresh-water": "Fresh water (20 °C)",
    seawater: "Seawater",
    mercury: "Mercury (20 °C)",
    ethanol: "Ethanol (20 °C)",
    glycerol: "Glycerol (20 °C)",
    air: "Air (sea level, standard atmosphere)",
  },
  planetNames: {
    sun: "Sun",
    mercury: "Mercury",
    venus: "Venus",
    earth: "Earth (standard gravity)",
    moon: "Moon",
    mars: "Mars",
    jupiter: "Jupiter",
    saturn: "Saturn",
    uranus: "Uranus",
    neptune: "Neptune",
    pluto: "Pluto",
  },
  solveFor: "Solve for",
  unitFor: "Unit for {name}",
  objectDensity: "Object density ρ",
  objectMass: "Object mass m",
  objectVolume: "Object volume V",
  fluidDensity: "Fluid density ρ",
  gravity: "Gravity g",
  buoyantForceBox: "Buoyant force F_b",
  displacedVolume: "Volume of fluid displaced V",
  displacedHint: "The part of the object that is below the surface.",
  forceShort: "Buoyant force",
  volumeShort: "Displaced volume",
  densityShort: "Fluid density",
  sigFigs: "Significant figures",
  sigFigsMessage:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  verdict: "The object",
  floats: "Floats",
  sinks: "Sinks",
  neutral: "Hovers (neutrally buoyant)",
  fractionSubmerged: "Share of volume below the surface",
  weight: "Weight in air W = mg",
  buoyantForce: "Buoyant force acting",
  apparentWeight: "Apparent weight, fully underwater",
  holdDown: "Hold-down force to keep it fully underwater",
  reserve: "Extra load it can carry before sinking",
  submergedVolume: "Submerged volume",
  displacedMass: "Mass of fluid displaced",
  working: "The working",
  floatCaption:
    "A floating object sinks until the fluid it displaces weighs as much as it does; a denser one sinks and only loses weight.",
  forceCaption:
    "The upward force equals the weight of the fluid pushed out of the way, whatever the object is made of.",
  diagramLabel: "Forces on an object in a fluid",
  diagramDescription:
    "A block in a tank of fluid with a downward arrow W for its weight and an upward arrow F-b for the buoyant force. A floating block sits partly above the waterline; a sinking block rests on the bottom.",
  errorMissing:
    "Fill in every box except the one you are solving for. Scientific notation such as 5e-3 works.",
  errorPositive:
    "Mass, volume, densities, gravity and force must all be greater than zero.",
  errorRange:
    "Those numbers are too large or too small to calculate. Check the units.",
  assumptions:
    "Assumes a still fluid of uniform density and an object whose density is uniform (a hollow boat floats because its average density is below the fluid's — enter its total mass and the volume it would displace when submerged). Surface tension, air trapped on the object and the buoyancy of the air around it are ignored.",
} as const;

type Mode = "float" | "force";

export default function BuoyancyCalculatorWidget() {
  const strings = useToolUi(BUOYANCY_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("float");
  const [sigChoice, setSigChoice] = useState<SigChoice>("4");
  const sig = Number(sigChoice);

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="bc-mode"
        onChange={setMode}
        options={[
          { value: "float", label: strings.modeFloat },
          { value: "force", label: strings.modeForce },
        ]}
        value={mode}
      />
      {mode === "float" ? (
        <FloatPanel sig={sig} strings={strings} />
      ) : (
        <ForcePanel sig={sig} strings={strings} />
      )}
      <SigFigsField
        format={fmt}
        id="bc-sig"
        label={strings.sigFigs}
        locale={locale}
        message={strings.sigFigsMessage}
        onChange={setSigChoice}
        value={sigChoice}
      />
      <Notice>{strings.assumptions}</Notice>
    </>
  );
}
